import {
  ButtonProps,
  Icon,
  Menu,
  MenuRootProps,
  Portal,
  useMenuContext,
} from '@chakra-ui/react';
import { IConfirmModalProps } from '@monetize/types/app';
import { FC, Fragment, useRef, useState } from 'react';
import { IconType } from 'react-icons/lib';
import { MdMoreVert } from 'react-icons/md';
import { handleApiErrorToast } from '../../../api/axios';
import { useConfirmModal } from '../../../services/confirmModal';
import { MBox, MCenter, MFlex, MIcon } from '../chakra';
import { CloseOnScroll } from '../CloseOnScroll';
import MCircularProgress from '../MCircularProgress';
import { MCustomIconButtonProps } from '../MCustomIconButton';
import MSwitch from '../MSwitch';
import { MTooltip } from '../MTooltip';
import { MText } from './..';

export type ActionType = {
  title?: string;
  icon?: IconType;
  action?: () => any | Promise<void>;
  color?: string;
  enabled?: boolean;
  confirmProps?: Partial<IConfirmModalProps> | null;
  loading?: boolean;
  actionElement?: React.ReactElement;
  isDisabled?: boolean;
  tooltipMsg?: string;
  hasTopDivider?: boolean;
  hasBottomDivider?: boolean;
  checked?: boolean;
};

interface DataTableActionsProps {
  actions: ActionType[];
  iconContainerProps?: Partial<MCustomIconButtonProps>;
  showPlaceholderIfNoItems?: boolean;
  variant?: MenuRootProps['variant'];
  menuOffset?: NonNullable<MenuRootProps['positioning']>['offset'];
  // `value` is omitted — `Menu.Trigger` isn't a real `Button`, just styled as
  // one, and its own `value` (Ark UI's menu item value type) is `string`
  // only, narrower than a native button's `value` attribute.
  buttonProps?: Omit<ButtonProps, 'value'>;
}

interface DataTableActionsContentProps extends Pick<
  DataTableActionsProps,
  'actions' | 'iconContainerProps'
> {
  triggerBtnRef: React.RefObject<HTMLButtonElement>;
  buttonProps?: Omit<ButtonProps, 'value'>;
}

const DataTableActionsContent: FC<DataTableActionsContentProps> = ({
  actions,
  iconContainerProps,
  triggerBtnRef,
  buttonProps,
}) => {
  const { open, setOpen } = useMenuContext();
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();
  const [actionLoading, setActionLoading] = useState(false);
  const onClose = () => setOpen(false);

  const confirmAction = (
    confirmProps: Partial<IConfirmModalProps>,
    action: () => void,
  ) => {
    const onYes = async () => {
      setModalLoading(true);
      try {
        action && (await action());
      } catch (err) {
        handleApiErrorToast(err);
      }
      setModalLoading(false);
      closeModal();
    };

    showConfirmModal({
      onYes,
      yesBtnProps: {
        variant: 'delete',
      },
      noBtnProps: {
        variant: 'cancel',
      },
      finalFocusRef: triggerBtnRef,
      ...confirmProps,
    });
  };

  return (
    <>
      {open && <CloseOnScroll onClose={onClose} />}
      <Menu.Trigger
        type="button"
        ref={triggerBtnRef}
        data-testid="data-table-actions"
        className="table-dropdown-actions"
        onClick={(e) => {
          e.stopPropagation();
        }}
        // Focus / active no longer paint a background — the keyboard halo
        // is the only focus signal, matching every other button in the app.
        // MenuButton without `as={IconButton}` doesn't inherit the V2
        // Button theme, so the halo is wired explicitly here.
        borderRadius="sm"
        cursor="pointer"
        _focusVisible={{
          outline: 'none',
          boxShadow: 'focus',
        }}
        _hover={{
          backgroundColor: iconContainerProps?.isDisabled
            ? 'none'
            : 'tBlue.hover',
        }}
        {...buttonProps}
      >
        <Icon
          w={6}
          h={6}
          _hover={{
            color: iconContainerProps?.isDisabled
              ? 'none'
              : (iconContainerProps?.iconColorHover ?? ''),
          }}
          asChild
        >
          <MdMoreVert />
        </Icon>
      </Menu.Trigger>
      {open && (
        <Portal>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                {actions.map(
                  (
                    {
                      action,
                      title,
                      icon,
                      enabled,
                      color,
                      confirmProps,
                      loading,
                      actionElement,
                      isDisabled,
                      tooltipMsg,
                      hasTopDivider,
                      hasBottomDivider,
                      checked,
                    },
                    index,
                  ) => {
                    const handleAction = () => {
                      if (!action) {
                        return;
                      }

                      const maybePromise = confirmProps
                        ? confirmAction(confirmProps, action)
                        : action();
                      // For async actions that don't have a confirmation modal, the menu will stay open with a loading indicator until finished
                      if (maybePromise instanceof Promise) {
                        setActionLoading(true);
                        maybePromise.finally(() => {
                          setActionLoading(false);
                          onClose();
                        });
                      } else {
                        onClose();
                      }
                    };

                    if (!enabled) {
                      return null;
                    }
                    if (actionElement) {
                      return actionElement;
                    }

                    const textColor =
                      color === 'danger' ? 'tRed.base' : 'tPurple.dark';

                    return (
                      <Fragment key={title}>
                        {hasTopDivider && <Menu.Separator />}
                        <Menu.Item
                          onClick={(ev) => ev.stopPropagation()}
                          onSelect={!isDisabled ? handleAction : undefined}
                          key={title}
                          color={textColor}
                          display="flex"
                          alignItems="center"
                          data-testid={`data-table-actions-${title}`}
                          disabled={isDisabled}
                          value={title ?? `action-${index}`}
                        >
                          <MTooltip
                            label={tooltipMsg}
                            placement="bottom-end"
                            isDisabled={!tooltipMsg}
                          >
                            <MFlex alignItems="center" w="100%">
                              {typeof checked !== 'boolean' && icon && (
                                <MIcon as={icon} w={3} h={3} mr={2} />
                              )}
                              <MText color="inherit">{title}</MText>
                              {typeof checked === 'boolean' && (
                                <MSwitch
                                  isChecked={checked}
                                  isReadOnly
                                  size="sm"
                                  ml="auto"
                                  pointerEvents="none"
                                />
                              )}
                              {loading && (
                                <MCircularProgress
                                  isIndeterminate
                                  size="2xs"
                                  ml="auto"
                                />
                              )}
                            </MFlex>
                          </MTooltip>
                        </Menu.Item>
                        {hasBottomDivider && <Menu.Separator />}
                      </Fragment>
                    );
                  },
                )}
                {actionLoading && (
                  <MBox
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    backgroundColor="white"
                    opacity={0.75}
                  >
                    <MCenter height="100%">
                      <MCircularProgress isIndeterminate size="sm" />
                    </MCenter>
                  </MBox>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Portal>
      )}
    </>
  );
};

export const DataTableActions: FC<DataTableActionsProps> = ({
  actions,
  iconContainerProps,
  showPlaceholderIfNoItems = false,
  variant = 'outline',
  menuOffset = { mainAxis: 0, crossAxis: 0 },
  buttonProps,
}: DataTableActionsProps) => {
  const triggerBtnRef = useRef<HTMLButtonElement>(null);

  // if there are no active links hide the dropdown all together
  const activeLinks = actions.filter((a) => a.enabled);
  if (activeLinks.length === 0) {
    return showPlaceholderIfNoItems ? <MBox minW="24px" /> : null;
  }

  return (
    <Menu.Root
      lazyMount
      unmountOnExit
      variant={variant}
      closeOnSelect={false}
      positioning={{
        offset: menuOffset,
        strategy: 'fixed',
        placement: 'bottom-end',
      }}
    >
      <DataTableActionsContent
        actions={actions}
        iconContainerProps={iconContainerProps}
        triggerBtnRef={triggerBtnRef}
        buttonProps={buttonProps}
      />
    </Menu.Root>
  );
};

export default DataTableActions;
